import { CalendarClock, FileClock, Trash2, Banknote, Construction } from "lucide-react";
import { it, enGB, es, de, sq, ro } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDate, fmtDateTime, fmtTime } from "@/lib/format";
import { getLang } from "@/i18n";
import { PALETTE, collabColor, apptColor, LEGEND, SIMPLE_LEGEND, SCADENZA_COLOR, CANTIERE_COLOR, PAGAMENTO_COLOR } from "@/components/agenda/palette";

const TYPE = {
  scadenza: { color: SCADENZA_COLOR, Icon: FileClock, cls: "text-violet-500" },
  cantiere: { color: CANTIERE_COLOR, Icon: Construction, cls: "text-orange-500" },
  pagamento: { color: PAGAMENTO_COLOR, Icon: Banknote, cls: "text-red-500" },
};
const dayOnly = (t) => t !== "appuntamento";

const DF = { it, en: enGB, es, de, sq, ro };

const ColorDot = ({ color, editable, onPick, testid }) => {
  const dot = <span className="block w-3.5 h-3.5 rounded-full shrink-0 ring-2 ring-background shadow" style={{ background: color }} />;
  if (!editable) return dot;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="rounded-full hover:scale-110 transition-transform" data-testid={testid} title="Cambia colore">{dot}</button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="grid grid-cols-5 gap-1.5">
          {PALETTE.map((c) => (
            <button key={c} onClick={() => onPick(c)} className={`w-7 h-7 rounded-full hover:scale-110 transition-transform ${c === color ? "ring-2 ring-offset-2 ring-foreground" : ""}`}
              style={{ background: c }} data-testid={`${testid}-opt-${c.slice(1)}`} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Upcoming = ({ items, isAdmin, isJunior, selected, toggleSel, onOpen, onOpenOther, onDelete }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="px-4 py-3 border-b border-border font-heading font-semibold text-sm">{isJunior ? "I miei sopralluoghi" : "Prossimi impegni"}</div>
    <div className="divide-y divide-border max-h-[340px] overflow-y-auto">
      {items.map((e) => (
        <div key={e.id} onClick={() => (e.type === "appuntamento" ? onOpen(e.raw) : onOpenOther?.(e))}
          className="px-4 py-3 flex items-start gap-2.5 group cursor-pointer hover:bg-secondary/50"
          data-testid={`agenda-upcoming-${e.id}`}>
          {isAdmin && e.type === "appuntamento" && (
            <input type="checkbox" onClick={(ev) => ev.stopPropagation()} checked={selected.includes(e.id)} onChange={() => toggleSel(e.id)}
              className="accent-primary w-4 h-4 mt-1 shrink-0" data-testid={`bulk-select-${e.id}`} />
          )}
          <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: TYPE[e.type]?.color || apptColor(e.raw) }} />
          {(() => { const T = TYPE[e.type]; return T ? <T.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${T.cls}`} /> : <CalendarClock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />; })()}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{e.title}</div>
            {e.sub && <div className="text-xs text-muted-foreground truncate">{e.sub}</div>}
            <div className="text-xs text-primary font-medium mt-0.5">{dayOnly(e.type) ? fmtDate(e.date) : fmtDateTime(e.date)}{e.type === "appuntamento" && e.raw?.data_fine ? ` \u2013 ${fmtTime(e.raw.data_fine)}` : ""}</div>
          </div>
          {!isJunior && e.type === "appuntamento" && (
            <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
              onClick={(ev) => { ev.stopPropagation(); onDelete(e.id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
          )}
        </div>
      ))}
      {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{isJunior ? "Nessun sopralluogo assegnato." : "Nessun impegno in programma."}</div>}
    </div>
  </div>
);

export const AgendaSidebar = ({ date, onPickDate, collabUsers, collabColors, onColorChange, isAdmin, simple = false, ...upcomingProps }) => (
  <aside className="space-y-4" data-testid="agenda-sidebar">
    <div className="rounded-xl border border-border bg-card p-2 flex justify-center">
      <Calendar mode="single" selected={date} onSelect={(d) => d && onPickDate(d)} month={date} onMonthChange={onPickDate}
        locale={DF[getLang()]} weekStartsOn={1} data-testid="agenda-mini-calendar" />
    </div>
    {!upcomingProps.isJunior && (
      <div className="rounded-xl border border-border bg-card p-4 space-y-2" data-testid="agenda-type-legend">
        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Legenda</div>
        {(simple ? SIMPLE_LEGEND : LEGEND).map((l) => <div key={l.label} className="flex items-center gap-2.5 text-sm"><span className="w-3.5 h-3.5 rounded" style={{ background: l.color }} /> {l.label}</div>)}
      </div>
    )}
    {isAdmin && collabUsers.length > 0 && (
      <div className="rounded-xl border border-border bg-card p-4 space-y-2.5" data-testid="agenda-collab-legend">
        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Collaboratori (bordo {simple ? "appuntamento" : "sopralluogo"})</div>
        {collabUsers.map((u) => (
          <div key={u.id} className="flex items-center gap-2.5 text-sm">
            <ColorDot color={collabColor(u.id, collabColors)} editable onPick={(c) => onColorChange(u.id, c)} testid={`collab-color-${u.id}`} />
            <span className="truncate">{u.name || u.email}</span>
          </div>
        ))}
      </div>
    )}
    <Upcoming isAdmin={isAdmin} {...upcomingProps} />
  </aside>
);

export default AgendaSidebar;
